import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

const AddressEdit = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const returnTo = location.state?.returnTo;
  const product = location.state?.product;
  const cartIds = location.state?.cartIds || [];

  const { id } = useParams(); // URL 파라미터에서 배송지 ID 추출

  // Flask의 address_data 구조와 동일하게 초기 상태 설정
  const [formData, setFormData] = useState({
    id: '',
    receiver_name: '',
    receiver_phone: '',
    address: '',
    detail_address: '',
    is_default: false
  });

  // 상세주소 비활성화 상태 관리
  const [isDetailDisabled, setIsDetailDisabled] = useState(true);

  useEffect(() => {
    // 1. Daum 우편번호 API 스크립트 로드
    if (!window.daum) {
      const script = document.createElement("script");
      script.src = "https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js";
      script.async = true;
      document.body.appendChild(script);
    }

    // 2. 기존 데이터 불러오기 (Flask GET 로직 대응)
    const fetchAddressData = async () => {
      try {
        const res = await axios.get(`/api/address/edit?id=${id}`); // 상세 조회 API 필요
        const data = res.data.address_data;
        
        setFormData({
          id: data.id,
          receiver_name: data.receiver_name,
          receiver_phone: data.receiver_phone,
          address: data.address,
          detail_address: data.detail_address || '',
          is_default: data.is_default === 1
        });

        // 원본 로직: 상세주소가 있으면 disabled 해제
        if (data.address) {
          setIsDetailDisabled(false);
        }
      } catch (err) {
        alert("데이터를 불러오는 데 실패했습니다.");
        navigate('/address/list');
      }
    };

    fetchAddressData();
  }, [id, navigate]);

  // 카카오 주소 API 실행 (원본: execDaumPostcode)
  const handleAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: (data) => {
        setFormData((prev) => ({ ...prev, address: data.address }));
        setIsDetailDisabled(false);
      }
    }).open();
  };

  // 폼 제출 (원본: action="/address/edit" method="POST")
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Flask 백엔드 API에 POST 요청 (데이터 구조 유지)
      const res = await axios.post('/api/address/edit', formData);
      if (res.data.success) {
        alert("배송 정보가 수정되었습니다.");
        navigate('/address/list', {
          state: {
            returnTo,
            product,
            cartIds
          }
        });
      }
    } catch (err) {
      alert("수정 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="container mt-4" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h3 className="mb-4">배송지 수정</h3>

      <form onSubmit={handleSubmit}>
        {/* hidden id field */}
        <input type="hidden" name="id" value={formData.id} />

        {/* 수령인 이름 */}
        <div className="form-floating mb-3" style={styles.floatingGroup}>
          <input
            type="text"
            className="form-control"
            id="receiver_name"
            placeholder="이름"
            required
            style={styles.input}
            value={formData.receiver_name}
            onChange={(e) => setFormData({ ...formData, receiver_name: e.target.value })}
          />
          <label htmlFor="receiver_name">이름</label>
        </div>

        {/* 전화번호 */}
        <div className="form-floating mb-3" style={styles.floatingGroup}>
          <input
            type="text"
            className="form-control"
            id="receiver_phone"
            placeholder="전화번호"
            required
            style={styles.input}
            value={formData.receiver_phone}
            onChange={(e) => setFormData({ ...formData, receiver_phone: e.target.value })}
          />
          <label htmlFor="receiver_phone">전화번호</label>
        </div>

        {/* 주소 */}
        <div className="form-floating mb-3" style={styles.floatingGroup}>
          <input
            type="text"
            className="form-control"
            id="sample5_address"
            placeholder="주소"
            readOnly
            style={{ ...styles.input, cursor: 'pointer' }}
            value={formData.address}
            onClick={handleAddressSearch}
          />
          <label htmlFor="sample5_address">주소</label>
        </div>

        {/* 상세주소 */}
        <div className="form-floating mb-3" style={styles.floatingGroup}>
          <input
            type="text"
            className="form-control"
            id="detailAddress"
            placeholder="상세주소"
            disabled={isDetailDisabled}
            style={styles.input}
            value={formData.detail_address}
            onChange={(e) => setFormData({ ...formData, detail_address: e.target.value })}
          />
          <label htmlFor="detailAddress">상세주소</label>
        </div>

        {/* 기본 배송지 여부 */}
        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="is_default"
            checked={formData.is_default}
            onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="is_default">
            기본 배송지로 설정
          </label>
        </div>

        <div className="text-end">
          <button type="submit" className="btn btn-primary" style={styles.btnPrimary}>수정</button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            style={styles.btnSecondary}
            onClick={() =>
              navigate('/address/list', {
                state: {
                  returnTo,
                  product,
                  cartIds
                }
              })
            }
          >
            취소
          </button>
        </div>
      </form>
    </div>
  );
};

// 스타일 (원본 Bootstrap 디자인 재현)
const styles = {
  floatingGroup: { position: 'relative' },
  input: {
    width: '100%',
    height: '58px',
    padding: '1.625rem 0.75rem 0.625rem',
    fontSize: '1rem',
    border: '1px solid #ced4da',
    borderRadius: '0.25rem',
    display: 'block'
  },
  btnPrimary: {
    padding: '8px 24px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    marginRight: '8px',
    cursor: 'pointer'
  },
  btnSecondary: {
    padding: '8px 24px',
    backgroundColor: '#6c757d',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  }
};

export default AddressEdit;